
/**
 * Module dependencies.
 */

import { colors } from 'src/styles/colors';
import { getModuleTheme } from 'src/core/utils/colors';
import { ifProp, prop, theme } from 'styled-tools';
import { isExternalRoute } from 'src/core/utils/routes';
import { media, units } from 'src/styles/utils';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Fill from 'src/components/core/layout/fill';
import Image from 'src/components/core/image';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import Type from 'src/components/core/typography';
import map from 'lodash/map';
import pick from 'lodash/pick';
import shuffle from 'lodash/shuffle';
import size from 'lodash/size';
import styled, { css } from 'styled-components';

/**
 * Colors theme config.
 */

const colorsThemeConfig = {
  dark: {
    textColor: colors.grey400,
    titleColor: colors.black
  },
  light: {
    textColor: colors.white,
    titleColor: colors.white
  }
};

/**
 * `AmbientProps` type.
 */

type AmbientProps = {
  buttonLabel: string,
  buttonUrl: string,
  description: string,
  id: string,
  images: string[],
  name: string
};

/**
 * `Props` type.
 */

type Props = {
  ambients: AmbientProps[],
  bgColor: string,
  className?: string
  isKiosk?: boolean,
  label: string
};

/**
 * `Section` styled component.
 */

const Section = styled.section`
  padding-bottom: ${units(13)};
  position: relative;

  ${media.min('ms')`
    padding-bottom: ${units(25)};
  `}
`;

/**
 * `Header` styled component.
 */

const Header = styled.div<{ bgColor: string }>`
  background-color: ${prop('bgColor')};
  padding: ${units(6)} 0 ${units(5)};

  ${media.min('ms')`
    padding: ${units(13)} 0 ${units(8)};
  `}
`;

/**
 * `HeaderGrid` styled component.
 */

const HeaderGrid = styled.div`
  display: grid;
  grid-template-areas:
    'title'
    'subtitle'
    'word';
  grid-template-columns: 100%;

  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      'title    title    title'
      'subtitle subtitle subtitle'
      'word     word     word';
    grid-template-columns: repeat(3, 4fr);
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.Paragraph).attrs({ as: 'p' })<{ color: string }>`
  color: ${prop('color')};
  font-style: italic;
  font-weight: 500;
  grid-area: title;
  margin-bottom: ${units(1)};
  text-align: center;
  text-transform: uppercase;
`;

/**
 * `Subtitle` styled component.
 */

const Subtitle = styled(Type.H3).attrs({ as: 'h2' })<{ color: string }>`
  color: ${prop('color')};
  grid-area: subtitle;
  margin-bottom: ${units(0.5)};
  text-align: center;
`;

/**
 * `WordWrapper` styled component.
 */

const WordWrapper = styled.div`
  grid-area: word;
  overflow: hidden;
  position: relative;
  text-align: center;
`;

/**
 * `Word` styled component.
 */

const Word = styled(Type.H3)<{
  color: string,
  visible: boolean
}>`
  animation-duration: 0.4s;
  animation-fill-mode: both;
  animation-timing-function: ${theme('animations.bezier.easeInOutSine')};
  color: ${prop('color')};
  opacity: 0;
  white-space: nowrap;

  ${ifProp('visible', css`
    animation-name: ${theme('keyframes.fadeInUp')};
  `, css`
    animation-name: ${theme('keyframes.fadeOutUp')};
  `)}
`;

/**
 * `Content` styled component.
 */

const Content = styled.div<{ bgColor: string }>`
  position: relative;

  &::before {
    background-color: ${prop('bgColor')};
    content: '';
    height: ${units(30)};
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: -1px;
  }
`;

/**
 * `ContentGrid` styled component.
 */

const ContentGrid = styled.div`
  display: grid;
  grid-template-areas:
    'gallery'
    'button';
  grid-template-columns: 100%;
  min-height: ${units(50)};
  position: relative;

  ${media.min('ms')`
    grid-template-areas:
      '. gallery .'
      '. button  .';
    grid-template-columns: 1fr 10fr 1fr;
  `}
`;

/**
 * `Gallery` styled component.
 */

const Gallery = styled.div`
  display: grid;
  grid-area: gallery;
  grid-gap: 10px;
  grid-template-columns: repeat(2, 1fr);

  ${media.min('ms')`
    grid-template-columns: repeat(4, 1fr);
  `}

  ${media.min('lg')`
    grid-gap: ${units(2)};
  `}
`;

/**
 * `ImageAnimation` styled component.
 */

const ImageAnimation = styled(Fill)<{ visible: boolean }>`
  animation-duration: 0.8s;
  animation-fill-mode: both;
  animation-timing-function: ${theme('animations.bezier.easeInOutSine')};
  opacity: 0;
  transform: translateY(100%);

  ${ifProp('visible', css`
    animation-name: ${theme('keyframes.fadeInUp')};
  `, css`
    animation-name: ${theme('keyframes.fadeOutUp')};
  `)}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  display: block;
  overflow: hidden;
  padding-bottom: 100%;
  position: relative;

  ${[0, 0.4, 0.8, 1.2, 0.6, 0.2, 1, 1.4].map((delay, index: number) => css`
    &:nth-child(${index + 1}) ${ImageAnimation} {
      animation-delay: ${delay}s;
    }
  `)}

  ${media.max('ms')`
    &:nth-child(1),
    &:nth-child(4) {
      grid-row-end: span 14;
      padding-bottom: 125%;
    }

    &:nth-child(2),
    &:nth-child(7) {
      grid-row-end: span 10;
    }

    &:nth-child(3),
    &:nth-child(5) {
      grid-row-end: span 16;
      padding-bottom: 150%;
    }

    &:nth-child(6),
    &:nth-child(8) {
      grid-row-end: span 6;
      padding-bottom: 75%;
    }
  `}

  ${media.min('ms')`
    &:nth-child(1),
    &:nth-child(3) {
      grid-row-end: span 14;
    }

    &:nth-child(2),
    &:nth-child(8) {
      grid-row-end: span 10;
    }

    &:nth-child(4),
    &:nth-child(5) {
      grid-row-end: span 16;
    }

    &:nth-child(6),
    &:nth-child(7) {
      grid-row-end: span 6;
      padding-bottom: 75%;
    }
  `}
`;

/**
 * `ButtonWrapper` styled component.
 */

const ButtonWrapper = styled.div`
  align-self: center;
  grid-area: button;
  justify-self: center;
  padding-top: ${units(6)};

  ${media.min('ms')`
    padding-top: ${units(10)};
  `}
`;

/**
 * `AmbientsSection` component.
 */

const AmbientsSection = (props: Props): ReactElement => {
  const { ambients, bgColor, className, isKiosk, label } = props;
  const { translate } = useTranslate();
  const moduleTheme = getModuleTheme(bgColor) ?? 'dark';
  const { textColor, titleColor } = colorsThemeConfig[moduleTheme];
  const [activeIndex, setActiveIndex] = useState(0);
  const { data, total } = useMemo(() => {
    return {
      data: map(ambients, (ambient: AmbientProps) => ({
        ...ambient,
        images: shuffle(ambient?.images)
      })),
      total: size(ambients)
    };
  }, [ambients]);

  const { buttonLabel, buttonUrl } = useMemo(() => ({
    ...pick(data[activeIndex], ['buttonLabel', 'buttonUrl'])
  }), [activeIndex, data]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (activeIndex === total - 1) {
        setActiveIndex(0);

        return;
      }

      setActiveIndex(activeIndex + 1);
    }, 10000);

    return () => clearInterval(interval);
  }, [activeIndex, total]);

  return (
    <Section className={className}>
      <Header bgColor={bgColor}>
        <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
          <Container hasPadding={!isKiosk}>
            <HeaderGrid>
              <Title color={titleColor}>
                {label}
              </Title>

              <Subtitle color={textColor}>
                {translate('common:sections.ambients.title')}
              </Subtitle>

              {map(data, ({ name }, index: number) => (
                <WordWrapper key={index}>
                  <Word
                    color={textColor}
                    visible={activeIndex === index}
                  >
                    {name}
                  </Word>
                </WordWrapper>
              ))}
            </HeaderGrid>
          </Container>
        </ParallaxFadeInUp>
      </Header>

      <Content bgColor={bgColor}>
        <FadeInUpAnimation>
          <Container hasPadding={!isKiosk}>
            <ContentGrid>
              {map(data, ({ id, images }, index: number) => (
                <Gallery key={id}>
                  {images.map((imageUrl: string, itemIndex: number) => (
                    <ImageWrapper key={itemIndex}>
                      <ImageAnimation visible={activeIndex === index}>
                        <Image
                          alt={label}
                          layout={'fill'}
                          objectFit={'cover'}
                          src={imageUrl}
                        />
                      </ImageAnimation>
                    </ImageWrapper>
                  ))}
                </Gallery>
              ))}

              <ButtonWrapper>
                <Button
                  aria-label={buttonLabel}
                  {...!isExternalRoute(buttonUrl) ? {} : {
                    rel: 'noopener',
                    target: '_blank'
                  }}
                  href={buttonUrl}
                >
                  {buttonLabel}
                </Button>
              </ButtonWrapper>
            </ContentGrid>
          </Container>
        </FadeInUpAnimation>
      </Content>
    </Section>
  );
};

/**
 * Export `AmbientsSection` component.
 */

export default AmbientsSection;
